/**
 * Button Component
 * Versatile button component with multiple variants, sizes, and icon support
 * Fully migrated to CSS Modules for architectural consistency
 */

import React from "react";
import styles from "./Button.module.css";

export type ButtonVariant =
  | "primary"
  | "secondary"
  | "tertiary"
  | "link-color"
  | "link-gray"
  | "destructive"
  | "gradient";

export type ButtonSize = "sm" | "md" | "lg" | "xl";

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  iconOnly?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  loading?: boolean;
}

export const Button: React.FC<ButtonProps> = ({
  variant = "primary",
  size = "md",
  fullWidth = false,
  iconOnly = false,
  leftIcon,
  rightIcon,
  children,
  loading = false,
  disabled = false,
  className = "",
  ...props
}) => {
  const isIconOnly = Boolean(
    iconOnly || (!children && (leftIcon || rightIcon)),
  );

  // Build class names using CSS Modules with BEM naming
  const buttonClasses = [
    styles.button,
    styles[`button--${variant}`],
    styles[`button--${size}`],
    fullWidth && styles["button--fullWidth"],
    isIconOnly && styles["button--iconOnly"],
    isIconOnly && styles[`button--iconOnly-${size}`],
    loading && styles["button--loading"],
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const iconClasses = [
    styles.button__icon,
    styles[`button__icon--${size}`],
  ].join(" ");

  const renderContent = () => {
    if (isIconOnly) {
      return leftIcon || rightIcon || children;
    }

    return (
      <>
        {leftIcon && <span className={iconClasses}>{leftIcon}</span>}
        {children && <span className={styles.button__content}>{children}</span>}
        {rightIcon && <span className={iconClasses}>{rightIcon}</span>}
        {loading && <span className={styles.button__spinner} />}
      </>
    );
  };

  return (
    <button className={buttonClasses} disabled={disabled || loading} {...props}>
      {renderContent()}
    </button>
  );
};

Button.displayName = "Button";
